/* @flow */

import get from 'lodash/get';
import isValidCpf from '@brazilian-utils/is-valid-cpf';
import moment from 'moment';
import invariant from 'invariant';
import { route } from 'preact-router';
import { createSelector } from 'reselect';
import { selecionarCliente } from './cliente';
import sleep from '../../utils/sleep';
import buscarContatos from '../../usuario/buscar-contatos';
import { getGridClientes } from './grid-clientes';
import {
    NIVEL_CLIENTE,
    NOME_CONTATO_RESIDENCIAL,
    NOME_CONTATO_CELULAR,
    NOME_CONTATO_COMERCIAL,
} from '../../usuario/constants';
import execDynamicRegExp from '../../usuario/exec-dynamic-regexp';
import formatarTelefone from '../../usuario/formatar-telefone';
import { type StateCadastroCliente, type AlertaCadastroCliente } from './types';
import { type GetState, type State } from '../../preact-app/types';
import { type Contato, type Usuario } from '../../usuario/types';
import cadastrarOuEditarOuDeletarContato from '../../usuario/cadastrar-deletar-ou-editar-contatos';
import separarDddNumero from '../../usuario/separar-ddd-numero';
import titleize from '../../utils/titleize';
import dataBrasileiraParaAmericana from '../../utils/data/brasileira-para-americana';
import isDataBrasileira from '../../utils/data/is-data-brasileira';
import api from '../../utils/api';

type SetContatosAction = {
    type: 'balcao/cadastro-cliente/SET_CONTATOS',
    contatos: Contato[],
};

type SetTentouEnviar = {
    type: 'balcao/cadastro-cliente/SET_TENTOU_ENVIAR',
    tentouEnviar: boolean,
};

type SetClienteAction = {
    type: 'balcao/cadastro-cliente/SET_CLIENTE',
} & Usuario;

type AlterarCadastroCompletoAction = {
    type: 'balcao/cadastro-cliente/ALTERAR_CADASTRO_COMPLETO',
};


type AlertarAction = {
    type: 'balcao/cadastro-cliente/ALERTAR',
    alerta: ?AlertaCadastroCliente,
};

type SetValorPorNome = {
    type: 'balcao/cadastro-cliente/SET_VALOR_POR_NOME',
    nome: string,
    valor: string | number,
};

type Action =
    | AlertarAction
    | SetClienteAction
    | SetValorPorNome
    | AlterarCadastroCompletoAction
    | SetContatosAction;

// Action Types

const SET_CONTATOS = 'balcao/cadastro-cliente/SET_CONTATOS';
const SET_CLIENTE = 'balcao/cadastro-cliente/SET_CLIENTE';
const ALTERAR_CADASTRO_COMPLETO = 'balcao/cadastro-cliente/ALTERAR_CADASTRO_COMPLETO';
const ALERTAR = 'balcao/cadastro-cliente/ALERTAR';
const SET_VALOR_POR_NOME = 'balcao/cadastro-cliente/SET_VALOR_POR_NOME';
const SET_TENTOU_ENVIAR = 'balcao/cadastro-cliente/SET_TENTOU_ENVIAR';

// Action Creators

export const setTentouEnviar = (tentouEnviar: boolean): SetTentouEnviar => ({
    type: SET_TENTOU_ENVIAR,
    tentouEnviar,
});

export const setContatos = (contatos: Contato[]) => ({
    type: SET_CONTATOS,
    contatos,
});

export const setCliente = (cliente: ?Usuario): SetClienteAction => ({
    type: SET_CLIENTE,
    ...cliente,
});

export const setValorPorNome = (nome: string, valor: string | number): SetValorPorNome => ({
    type: SET_VALOR_POR_NOME,
    valor,
    nome,
});

export const alterarCadastroCompleto = (): AlterarCadastroCompletoAction => ({
    type: ALTERAR_CADASTRO_COMPLETO,
});

const alertar = (alerta: ?AlertaCadastroCliente): AlertarAction => ({
    type: ALERTAR,
    alerta,
});

// Utils

type ContatosTipo = {
    residencial: ?Contato,
    celular: ?Contato,
    comercial: ?Contato,
};

const trocarNomesContato = (contatos: Contato[]) => {
    let temCelular = false;
    let temResidencial = false;

    return contatos.map((contato) => {
        if (contato.numero.length === 8 && !temResidencial) {
            temResidencial = true;
            return {
                ...contato,
                nome: NOME_CONTATO_RESIDENCIAL,
            };
        }

        if (contato.numero.length === 9 && !temCelular) {
            temCelular = true;
            return {
                ...contato,
                nome: NOME_CONTATO_CELULAR,
            };
        }

        return contato;
    });
};

const separarTiposContato = (contatos: Contato[]): ContatosTipo => trocarNomesContato(contatos)
    .reduce((acc, contato) => {
        const { nome } = contato;
        if (!nome) {
            return acc;
        }

        return {
            celular: execDynamicRegExp('celular', nome) ? contato : acc.celular,
            comercial: execDynamicRegExp('comercial', nome) ? contato : acc.comercial,
            residencial: execDynamicRegExp('residencial', nome) ? contato : acc.residencial,
        };
    }, {
        residencial: null,
        celular: null,
        comercial: null,
    });

const isCelular = (celular: string) => /^\(?[\d]{2}\)? ?[\d]{5}?-?[\d]{4}$/g.test(celular);
const isTelefone = (telefone: string) => /^\(?[\d]{2}\)? ?[\d]{4}?-?[\d]{4}$/g.test(telefone);

// Selectors

export const getCadastroCliente = (state: State) => state.balcao.cadastroCliente;
const getNome = (state: State) => getCadastroCliente(state).nome;
const getCpf = (state: State) => getCadastroCliente(state).cpf;
const getNascimento = (state: State) => getCadastroCliente(state).nascimento;
const getCelular = (state: State) => getCadastroCliente(state).celular;
const getResidencial = (state: State) => getCadastroCliente(state).residencial;
const getComercial = (state: State) => getCadastroCliente(state).comercial;
const getDadosCliente = (state: State) => {
    const {
        id,
        nome,
        cpf,
        email,
        nascimento,
    } = getCadastroCliente(state);
    return {
        id,
        nome,
        cpf: cpf || null,
        email: email || null,
        nascimento: nascimento || null,
        nivel: NIVEL_CLIENTE,
        origem: 'balcao',
    };
};
const getContatos: State => Contato[] = createSelector(
    getCadastroCliente,
    ({
        id,
        celular,
        residencial,
        comercial,
        celularId,
        comercialId,
        residencialId,
    }) => {
        if (!id) {
            return [];
        }
        const cel = separarDddNumero(celular);
        const com = separarDddNumero(comercial);
        const res = separarDddNumero(residencial);
        return [
            {
                id: celularId,
                usuariosId: id,
                nome: NOME_CONTATO_CELULAR,
                ddd: cel ? cel.ddd : '',
                numero: cel ? cel.numero : '',
            },
            {
                id: residencialId,
                usuariosId: id,
                nome: NOME_CONTATO_RESIDENCIAL,
                ddd: res ? res.ddd : '',
                numero: res ? res.numero : '',
            },
            {
                id: comercialId,
                usuariosId: id,
                nome: NOME_CONTATO_COMERCIAL,
                ddd: com ? com.ddd : '',
                numero: com ? com.numero : '',
            },
        ];
    },
);

export const isNomeValido: State => boolean = createSelector(
    getNome,
    nome => /[A-Z]+ [A-Z]+/i.test(nome),
);

export const isCpfValido: State => boolean = createSelector(
    getCpf,
    cpf => !cpf.replace(/\D/g, '') || isValidCpf(cpf),
);

export const isCelularValido: State => boolean = createSelector(
    getCelular,
    celular => !celular || isCelular(celular),
);

export const isResidencialValido: State => boolean = createSelector(
    getResidencial,
    telefone => !telefone || isTelefone(telefone),
);

export const isComercialValido: State => boolean = createSelector(
    getComercial,
    telefone => !telefone || isTelefone(telefone) || isCelular(telefone),
);

export const isNascimentoValido: State => boolean = createSelector(
    getNascimento,
    nascimento => !nascimento || (
        nascimento.replace(/\D/g, '').length === 8 && moment(nascimento, 'YYYY-MM-DD').isValid()
    ),
);

export const temUmTelefoneValido: State => boolean = createSelector(
    getCelular,
    getResidencial,
    (...args) => (args.some(Boolean)),
);

export const isFormularioValido: State => boolean = createSelector(
    isNomeValido,
    isCpfValido,
    isCelularValido,
    isResidencialValido,
    isComercialValido,
    isNascimentoValido,
    temUmTelefoneValido,
    (...args) => (args.every(Boolean)),
);

// Thunks

export const onInputSetValor = (e: Event) => (dispatch: Function) => {
    invariant(e.target instanceof HTMLInputElement, 'Erro');
    const { name, value } = e.target;
    if (name === 'nascimento' && isDataBrasileira(value)) {
        dispatch(setValorPorNome(name, dataBrasileiraParaAmericana(value)));
        return;
    }
    dispatch(setValorPorNome(name, value));
};

export const cadastroClienteDidMount = (clientesId: string, query: ?string) => (
    dispatch: Function,
    getState: GetState,
) => {
    dispatch(setTentouEnviar(false));

    if (clientesId === 'novo') {
        dispatch(setCliente(null));
        dispatch(setContatos([]));

        if (!query) {
            return;
        }

        if (isCelular(query)) {
            dispatch(setValorPorNome('celular', query));
        }

        if (isTelefone(query)) {
            dispatch(setValorPorNome('residencial', query));
        }

        if (/[A-Z]+ [A-Z]+/i.test(query)) {
            dispatch(setValorPorNome('nome', titleize(query)));
        }
        return;
    }

    const cliente = getGridClientes(getState())
        .itens
        .find(({ id }) => Number(clientesId) === id);

    if (!cliente) {
        global.history.back();
        return;
    }

    dispatch(setCliente(cliente));
    buscarContatos(cliente).then(({ data }) => dispatch(setContatos(data)));
};

const alertarTimeout = (alerta: AlertaCadastroCliente) => async (dispatch: Function) => {
    dispatch(alertar(alerta));
    await sleep(5000);
    dispatch(alertar(null));
};

const editar = () => async (dispatch: Function, getState: GetState) => {
    try {
        await api.editarUsuario(getDadosCliente(getState()));
        await Promise.all(getContatos(getState()).map(cadastrarOuEditarOuDeletarContato));
        route('/balcao/clientes/');
    } catch (error) {
        dispatch(alertarTimeout(get(error, 'response.data')));
    }
};

const cadastrar = () => async (dispatch: Function, getState: GetState) => {
    try {
        const { data: { id: clientesId } } = await api.criarUsuario(getDadosCliente(getState()));
        dispatch(setValorPorNome('id', clientesId));
        await Promise.all(getContatos(getState()).map(cadastrarOuEditarOuDeletarContato));
        const { data: cliente } = await api.getUsuarioPorId(clientesId);
        dispatch(selecionarCliente(cliente));
        route(`/balcao/clientes/${clientesId}/enderecos/novo`);
    } catch (error) {
        dispatch(alertarTimeout(get(error, 'response.data')));
    }
};

const isEdicao = createSelector(getDadosCliente, dados => !!dados.id);

export const enviarCadastroCliente = () => async (dispatch: Function, getState: GetState) => {
    dispatch(setTentouEnviar(true));
    if (!isFormularioValido(getState())) {
        return;
    }

    if (isEdicao(getState())) {
        dispatch(editar());
        return;
    }
    dispatch(cadastrar());
};

// Reducer

const initialState = {
    id: null,
    nome: '',
    email: '',
    cpf: '',
    nascimento: '',
    celular: '',
    residencial: '',
    comercial: '',
    residencialId: 0,
    celularId: 0,
    comercialId: 0,
    mostrarCadastroCompleto: false,
    alerta: null,
    tentouEnviar: false,
};

const cadastroClienteReducer = (
    state: StateCadastroCliente = initialState,
    action: Action,
): StateCadastroCliente => {
    switch (action.type) {
    case SET_CLIENTE:
        return {
            ...state,
            id: action.id,
            nome: action.nome || '',
            email: action.email || '',
            cpf: action.cpf || '',
            nascimento: action.nascimento || '',
        };
    case SET_CONTATOS: {
        const { residencial, celular, comercial } = separarTiposContato(action.contatos);

        return {
            ...state,
            residencialId: residencial ? residencial.id : 0,
            celularId: celular ? celular.id : 0,
            comercialId: comercial ? comercial.id : 0,
            residencial: residencial ? formatarTelefone(residencial) : '',
            celular: celular ? formatarTelefone(celular) : '',
            comercial: comercial ? formatarTelefone(comercial) : '',
        };
    }
    case ALTERAR_CADASTRO_COMPLETO:
        return {
            ...state,
            mostrarCadastroCompleto: !state.mostrarCadastroCompleto,
        };
    case SET_VALOR_POR_NOME:
        return {
            ...state,
            [action.nome]: action.valor,
        };
    case ALERTAR:
        return {
            ...state,
            alerta: action.alerta,
        };
    case SET_TENTOU_ENVIAR:
        return {
            ...state,
            tentouEnviar: action.tentouEnviar,
        };
    default:
        return state;
    }
};

export default cadastroClienteReducer;
